import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Hash, Pencil } from 'lucide-react';
import { extractError } from '@/lib/errors';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { ReferenceCombobox } from '@/components/orders/ReferenceCombobox';

/**
 * ReferenceEditor — Order Detail header widget for the running-number system.
 * Renders nothing when the company hasn't opted in.
 *
 * Shows the order's current number (or "No <term>"). Company Admin and Super
 * Admin get a pencil that opens a dialog to either mint a NEW number or link
 * the order to an EXISTING one. A number may be shared only across orders that
 * match on Customer + Goldsmith + Gold Purity, so the existing picker is
 * scoped to all three.
 *
 * Props:
 *   order      the order document (id, reference_no, customer_id, goldsmith_id, jewellery_purity_id)
 *   companyId  active company id
 *   canEdit    boolean — Company Admin or Super Admin
 *   onUpdated  () => void — reload callback
 */
export function ReferenceEditor({ order, companyId, canEdit, onUpdated }) {
  const { enabled, label, labelShort, refresh: refreshConfig } = useReferenceConfig(companyId);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('new');
  const [existingRef, setExistingRef] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!enabled) return null;

  const hasRef = order?.reference_no != null;
  // A number can be shared only when Customer + Goldsmith + Gold Purity all
  // match, so the "existing" picker needs all three present on this order.
  const canPickExisting = !!(order?.customer_id && order?.goldsmith_id && order?.jewellery_purity_id);

  const openDialog = () => {
    setMode(hasRef ? 'existing' : 'new');
    setExistingRef(null);
    setOpen(true);
  };

  const submit = async () => {
    if (mode === 'existing' && !existingRef) {
      toast.error(`Pick an existing ${label} first`);
      return;
    }
    setSaving(true);
    try {
      const payload = mode === 'new'
        ? { mode: 'new' }
        : { mode: 'existing', existing_reference_no: existingRef };
      const { data } = await api.patch(`/orders/${order.id}/reference-no`, payload);
      toast.success(`${label} set to ${data.reference_no}`);
      setOpen(false);
      refreshConfig();
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e, `Failed to update ${label}`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <span className="inline-flex items-center gap-1" data-testid="order-detail-reference">
        {hasRef ? (
          <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono text-[11px]">
            <Hash size={11} className="mr-0.5" />
            {labelShort} {order.reference_no}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 font-mono text-[11px]">
            <Hash size={11} className="mr-0.5" />
            No {label}
          </Badge>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={openDialog}
            className="inline-flex items-center rounded p-1 text-slate-400 hover:text-[var(--ink-navy)] hover:bg-slate-100 transition-colors"
            title={`Change ${label}`}
            data-testid="order-detail-reference-edit-button"
          >
            <Pencil size={12} />
          </button>
        )}
      </span>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="order-detail-reference-dialog">
          <DialogHeader>
            <DialogTitle>Change {label}</DialogTitle>
            <DialogDescription>
              {canPickExisting
                ? `Existing ${labelShort.toLowerCase()}s are limited to this order's customer, goldsmith and gold purity.`
                : 'To reuse an existing number, this order needs a customer, goldsmith and gold purity set.'}
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
            <label className="flex items-start gap-2 rounded-md border border-[var(--divider)] p-3 cursor-pointer hover:bg-slate-50">
              <RadioGroupItem value="new" id="ref-mode-new" className="mt-0.5" data-testid="reference-mode-new" />
              <div>
                <div className="text-sm font-medium text-slate-800">Generate a new {label}</div>
                <div className="text-[11px] text-slate-500">Mints the next per-company number.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 rounded-md border border-[var(--divider)] p-3 cursor-pointer hover:bg-slate-50">
              <RadioGroupItem value="existing" id="ref-mode-existing" className="mt-0.5" data-testid="reference-mode-existing" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800">Use an existing {label}</div>
                <div className="text-[11px] text-slate-500">Group this order under an existing job/set.</div>
                {mode === 'existing' && (
                  <div className="mt-2">
                    {canPickExisting ? (
                      <ReferenceCombobox
                        companyId={companyId}
                        customerId={order.customer_id}
                        goldsmithId={order.goldsmith_id}
                        purityId={order.jewellery_purity_id}
                        value={existingRef}
                        onChange={setExistingRef}
                        label={label}
                        testId="order-detail-reference-combobox"
                      />
                    ) : (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                        This order needs a customer, goldsmith and gold purity before you can link it to an existing {label}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submit}
              className="luxury-button"
              disabled={saving || (mode === 'existing' && (!canPickExisting || !existingRef))}
              data-testid="order-detail-reference-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReferenceEditor;
